import { Button, ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Label, Layout, LayoutLoadEvent, LookupModelSearchEvent, Panel, Snackbar, TableRow, TableRowDisplayEvent, TableRowExpansionEvent } from "@mcleod/components";
import { Alignment, Api, ModelRow, NumberUtil, StringUtil, UserSettings } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "../CommonDialogs";
import { UserLayoutProvider } from "../UserLayoutProvider";
import { UserTableLayout } from "./UserTableLayout";

interface UserActionConfig {
    action: Action;
    confirmationVerb: string;
    getDisabledTooltip: (actionableSelectionCount: number) => string;
    onSuccess?: (rows: TableRow[]) => void;
    getSuccessMessage: (count: number) => string;
    filterRows?: (row: TableRow) => boolean;
}

enum LockStatus {
    LOCKED = "L",
    UNLOCKED = "U",
    BANNED = "B",
    AUTO_LOCKED = "A"
}

enum Action {
    TOGGLE_ACTIVE = "toggle_is_active",
    LOCK = "lock",
    UNLOCK = "unlock",
    REVOKE = "revoke"
}

const MAX_BATCH_ACTION_SIZE = 25;

export class UserTableController  {
    private userTableLayout: UserTableLayout;
    private actionButtons: Button[];
    private actionConfigs: Record<string, UserActionConfig>;

    constructor(userTable: UserTableLayout) {
        this.userTableLayout = userTable;
        this.actionButtons = [this.userTableLayout.buttonRevoke, this.userTableLayout.buttonUnlock, this.userTableLayout.buttonLock, this.userTableLayout.buttonToggleActive];
        this.actionConfigs = this.createActionConfigs();
        this.userTableLayout.searchPanelUsers.collapse({ speed: 0 });
        this.userTableLayout.tableUsers.generalLayout = UserLayoutProvider.get().layoutPath;
        this.addTextboxSearchIdLookupListener();
        this.addTableEventListeners();
        this.addTableActionClickListeners();

        this.userTableLayout.textboxSearchIsActive.setSelectedItemFromValue("Y");
        this.userTableLayout.getSourceUsers().resetDefaultSearchRowToCurrent();

        this.userTableLayout.getSourceUsers().addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                event.filter.web_user_type = this.userTableLayout.getUserType();
            }
        });
    }

    private addTextboxSearchIdLookupListener() {
        this.userTableLayout.textboxSearchId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.web_user_type = this.userTableLayout.getUserType()
        });
    }

    private addTableEventListeners() {
        const tableUsers = this.userTableLayout.tableUsers;
        tableUsers.addSelectionListener(() => this.syncActionButtons());
        tableUsers.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const labelLockStatus = event.getTableRow().findComponentById("labelLockStatus") as Label;
            labelLockStatus.imageHeight = 18;
            this.syncLabelLockStatusImage(labelLockStatus);
            labelLockStatus?.addDataDisplayListener((event: DataDisplayEvent) => this.labelLockStatusOnDataDisplay(event));
        });
        tableUsers.addRowExpandListener((event: TableRowExpansionEvent) => this.onRowExpand(event));
    }

    private addTableActionClickListeners() {
        this.actionButtons.forEach(button => {
            const actionConfig = this.actionConfigs[button.id];
            if (!actionConfig) {
                throw new Error(`No action config found for button id: ${button.id}`);
            }
            button.addClickListener((event: ClickEvent) => this.executeUserAction(event, actionConfig));
        });
    }

    private createActionConfigs(): Record<string, UserActionConfig> {

        const createDisabledTooltip  = (
            noSelectionTooltip = "A user must be selected to perform this action.",
            maxSelectionTooltip = `A maximum of ${MAX_BATCH_ACTION_SIZE} users can be selected for this action.`
        ): ((rowCount: number) => string) => {
            return (rowCount: number) => {
                return rowCount > MAX_BATCH_ACTION_SIZE ? maxSelectionTooltip: noSelectionTooltip;
            };
        };

        return {
            [this.userTableLayout.buttonRevoke.id]: {
                action: Action.REVOKE,
                confirmationVerb: "invalidate",
                getDisabledTooltip: createDisabledTooltip(),
                getSuccessMessage: (count) => "Successfully invalidated " + StringUtil.pluralString(count, "user login token", "user login tokens") + "."
            },
            [this.userTableLayout.buttonUnlock.id]: {
                action: Action.UNLOCK,
                confirmationVerb: "unlock",
                getDisabledTooltip: createDisabledTooltip(
                    "A locked user must be selected to perform this action.",
                    `A maximum of ${MAX_BATCH_ACTION_SIZE} locked users can be selected for this action.`
                ),
                onSuccess: (rows) => this.updateLockStatus(rows, LockStatus.UNLOCKED),
                getSuccessMessage: (count) => "Successfully unlocked " + StringUtil.pluralString(count, "user", "users") + ".",
                filterRows: (row: TableRow) => this.isUserLocked(row)
            },
            [this.userTableLayout.buttonLock.id]: {
                action: Action.LOCK,
                confirmationVerb: "lock",
                getDisabledTooltip: createDisabledTooltip(
                    "An unlocked user must be selected to perform this action.",
                    `A maximum of ${MAX_BATCH_ACTION_SIZE} unlocked users can be selected for this action.`
                ),
                onSuccess: (rows) => this.updateLockStatus(rows, LockStatus.LOCKED),
                getSuccessMessage: (count) => "Successfully locked " + StringUtil.pluralString(count, "user", "users") + ".",
                filterRows: (row: TableRow) => !this.isUserLocked(row)
            },
            [this.userTableLayout.buttonToggleActive.id]: {
                action: Action.TOGGLE_ACTIVE,
                confirmationVerb: "toggle the active status for",
                getDisabledTooltip: createDisabledTooltip(),
                onSuccess: (rows) => {
                    rows.forEach(row => {
                        const current = row.data.get("is_active");
                        row.data.set("is_active", current === "Y" ? "N" : "Y");
                        row.displayComponentValues();
                    });
                },
                getSuccessMessage: (count) => "Successfully toggled active status for " + StringUtil.pluralString(count, "user", "users") + "."
            }
        };
    }

    private getActionableRows(config?: UserActionConfig) : TableRow[] {
        const rows = this.userTableLayout.tableUsers.getSelectedRowsMatching(row => !StringUtil.equalsIgnoreCase(row.data.get("id"), UserSettings.getUserId()));
        if (config?.filterRows) {
            return rows.filter(config.filterRows);
        }
        return rows;
    }

    private async executeUserAction(event: ClickEvent, config: UserActionConfig) {
        const button = event.target as Button;
        const rowsToProcess = this.getActionableRows(config);

        if (rowsToProcess.length === 0) {
            const message = button.disabledTooltip ?? "A user must be selected to perform this action.";
            button.showAdhocTooltip(message, { shaking: true, position: Alignment.RIGHT });
            return;
        }

        button.busy = true;
        this.actionButtons.forEach(button => button.enabled = false);
        try {
            const selectedIds = rowsToProcess.map(row => row.data.get("id"));

            if (!await this.showPromptIfNeeded(selectedIds, config.confirmationVerb)) {
                return;
            }

            const body = { user_ids: selectedIds, action: config.action };
            const response = await Api.post("user/batch-update", body);

            const failedIds: string[] = response?.failed_ids ?? [];
            const successfulRows = rowsToProcess.filter(row => !failedIds.includes(row.data.get("id")));

            if (successfulRows.length > 0) {
                config.onSuccess?.(successfulRows);
            }

            let message = config.getSuccessMessage(successfulRows.length);
            if (failedIds.length > 0) {
                message += `. Failed to update ${failedIds.length} ${StringUtil.pluralString(failedIds.length, "user", "users")}.`;
            }

            Snackbar.showSnackbar(message);
        } finally {
            button.busy = false;
            this.syncActionButtons();
        }
    }

    private updateLockStatus(rows: TableRow[], lockStatus: LockStatus) {
        rows.forEach(row => {
            row.data.set("lock_status", lockStatus);
            row.displayComponentValues();
        });
    }

    private async showPromptIfNeeded(selectedUserIds: string[], action: string) {
        if (selectedUserIds.length == 1) {
            return true;
        }
        const count = NumberUtil.formatInteger(selectedUserIds.length);
        return await CommonDialogs.showYesNo(`Are you sure you want to ${action} ${count} accounts?`);
    }

    private onRowExpand(expansionEvent: TableRowExpansionEvent) {
        if (!expansionEvent.isExpanding)
            return;
        const tableRow: TableRow = expansionEvent.target as TableRow;
        const expandPanel = expansionEvent.expandComponentParent as Panel;

        const layoutExpanded = expandPanel.findComponentById("layoutUserDevicesGridExpansion") as Layout;
        layoutExpanded?.addLayoutLoadListener((event: LayoutLoadEvent) => {
            event.target.mainDataSource.search({ user_id: tableRow.data.get("id") });
        });
    }

    private labelLockStatusOnDataDisplay(event: DataDisplayEvent) {
        this.syncLabelLockStatusImage(event.target as Label);
    }

    private syncLabelLockStatusImage(label: Label) {
        label.text = "";
        const lockStatus = label.getRelevantModelRow()?.get("lock_status", null);
        if (lockStatus == null || LockStatus.UNLOCKED === lockStatus) {
            label.imageName = null;
            label.tooltip = null;
        } else {
            label.imageName = ImageName.lockClosed;
            if (lockStatus === LockStatus.AUTO_LOCKED)
                label.tooltip = "This user was locked because they entered an incorrect password too many times.";
            else if (lockStatus === LockStatus.LOCKED)
                label.tooltip = "This user was locked manually.";
            else if (lockStatus === LockStatus.BANNED)
                label.tooltip = "This user was locked because they had suspicious activity.";
        }
    }

    private isUserLocked(row: TableRow | ModelRow): boolean {
        const modelRow = row instanceof TableRow ? row.data : row;
        const lockStatus = modelRow.get("lock_status");
        return lockStatus === LockStatus.LOCKED || lockStatus === LockStatus.BANNED || lockStatus === LockStatus.AUTO_LOCKED;
    }

    private syncActionButtons() {
        const actionableRows = this.getActionableRows();
        this.actionButtons.forEach(button => {
            const config = this.actionConfigs[button.id];
            let configRowCount = actionableRows.length;
            if (config?.filterRows) {
                configRowCount = actionableRows.filter(config.filterRows).length;
            }
            this.syncActionButtonProps(button, config, configRowCount);
        });
    }

    private syncActionButtonProps(button: Button, actionConfig: UserActionConfig, selectionCount: number) {
        button.enabled = selectionCount > 0 && selectionCount <= MAX_BATCH_ACTION_SIZE;
        button.disabledTooltip = actionConfig.getDisabledTooltip(selectionCount);
    }
}
